import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { GripVertical, Save, Loader2, Hash, ArrowLeft } from 'lucide-react';
import { Button } from '../components/ui/button';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Allows an admin to reorder auto-generated tag sections by drag & drop.
 * Saves via PUT /api/shop/tag-order.
 */
export default function TagOrderManager({ onClose }) {
  const { user } = useAuth();
  const [items, setItems] = useState([]); // [{tag, count}]
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dragIndex, setDragIndex] = useState(null);
  const [overIndex, setOverIndex] = useState(null);

  const fetchSections = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/home/sections`, { withCredentials: true });
      const sections = (data.tag_sections || []).map(s => ({ tag: s.tag, count: s.count }));
      setItems(sections);
    } catch { toast.error('Errore caricamento sezioni'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { if (user) fetchSections(); }, [user, fetchSections]);

  const onDragStart = (idx) => setDragIndex(idx);
  const onDragOver = (e, idx) => { e.preventDefault(); setOverIndex(idx); };
  const onDrop = (e, idx) => {
    e.preventDefault();
    if (dragIndex === null || dragIndex === idx) return;
    const next = [...items];
    const [moved] = next.splice(dragIndex, 1);
    next.splice(idx, 0, moved);
    setItems(next);
    setDragIndex(null);
    setOverIndex(null);
  };
  const onDragEnd = () => { setDragIndex(null); setOverIndex(null); };

  const move = (idx, dir) => {
    const ni = idx + dir;
    if (ni < 0 || ni >= items.length) return;
    const next = [...items];
    [next[idx], next[ni]] = [next[ni], next[idx]];
    setItems(next);
  };

  const save = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/shop/tag-order`, {
        tag_order: items.map(i => i.tag.toLowerCase())
      }, { withCredentials: true });
      toast.success('Ordine salvato');
      onClose?.();
    } catch { toast.error('Errore salvataggio'); }
    finally { setSaving(false); }
  };

  if (loading) return <div className="p-6 text-center"><Loader2 size={28} className="animate-spin mx-auto text-[#C25934]" /></div>;

  return (
    <div data-testid="tag-order-manager" className="space-y-3">
      <div className="flex items-center gap-2">
        {onClose && (
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-[#F0EBE1]">
            <ArrowLeft size={18} className="text-[#5C554F]" />
          </button>
        )}
        <div className="flex-1">
          <h3 className="font-semibold text-[#2A2624]" style={{ fontFamily: 'Outfit' }}>Ordina sezioni home</h3>
          <p className="text-xs text-[#877E78]">Trascina per riordinare le sezioni auto-generate dai tag</p>
        </div>
        <Button
          data-testid="save-tag-order"
          onClick={save}
          disabled={saving || items.length === 0}
          className="h-9 rounded-full bg-[#C25934] hover:bg-[#A64B2B] text-white text-sm"
        >
          {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} className="mr-1" />}
          Salva
        </Button>
      </div>

      {items.length === 0 ? (
        <div className="bg-[#F7F3EC] rounded-xl p-6 text-center text-sm text-[#877E78]">
          Nessun tag presente. Aggiungi tag ai prodotti per creare sezioni automatiche.
        </div>
      ) : (
        <div className="space-y-1.5">
          {items.map((it, idx) => (
            <div
              key={it.tag}
              data-testid={`drag-item-${it.tag}`}
              draggable
              onDragStart={() => onDragStart(idx)}
              onDragOver={(e) => onDragOver(e, idx)}
              onDrop={(e) => onDrop(e, idx)}
              onDragEnd={onDragEnd}
              className={`flex items-center gap-2 bg-white rounded-xl border p-2.5 cursor-grab active:cursor-grabbing transition-all ${
                dragIndex === idx ? 'opacity-40' : ''
              } ${overIndex === idx && dragIndex !== idx ? 'border-[#C25934] bg-[#C25934]/5' : 'border-[#E8E3D9]'}`}
            >
              <GripVertical size={18} className="text-[#877E78] flex-shrink-0" />
              <Hash size={14} className="text-[#C25934] flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <div className="font-semibold text-sm text-[#2A2624] capitalize truncate">{it.tag}</div>
                <div className="text-[10px] text-[#877E78]">{it.count} prodotti</div>
              </div>
              <div className="flex flex-col gap-0.5">
                <button
                  data-testid={`move-up-${it.tag}`}
                  onClick={() => move(idx, -1)}
                  disabled={idx === 0}
                  className="w-6 h-6 rounded-md bg-[#F0EBE1] hover:bg-[#E6E2D6] disabled:opacity-30 text-[#5C554F] text-xs"
                >↑</button>
                <button
                  data-testid={`move-down-${it.tag}`}
                  onClick={() => move(idx, 1)}
                  disabled={idx === items.length - 1}
                  className="w-6 h-6 rounded-md bg-[#F0EBE1] hover:bg-[#E6E2D6] disabled:opacity-30 text-[#5C554F] text-xs"
                >↓</button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
